import React from 'react';
import { Loader2, Star, AlertCircle, ShieldCheck, ArrowDown } from 'lucide-react';
import { fileUrl } from './useVisualizer';
import EmailCapture from './EmailCapture';
import ShareDesign from './ShareDesign';

const Frame = ({ src, label, testId, children }) => (
  <figure className="relative rounded-2xl overflow-hidden bg-[#f4efe9] aspect-[4/3]" data-testid={testId}>
    {src ? <img src={src} alt={label} className="w-full h-full object-cover" /> : children}
    <figcaption className="absolute top-3 left-3 text-[11px] uppercase tracking-wider font-semibold bg-white/90 text-[#3a190b] px-2.5 py-1 rounded-full">{label}</figcaption>
  </figure>
);

const ResultView = ({ session, favorite, setFavorite, onRequestQuote, onSessionUpdate }) => {
  const gens = session.generations;
  if (gens.length === 0) return null;
  const hasDone = gens.some((g) => g.status === 'done');
  return (
    <section className="space-y-6" data-testid="result-view">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-2xl bg-[#faf7f5] border border-[#3a190b]/10 p-5" data-testid="result-cta-bar">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-[#202020]">{hasDone ? 'Uw ontwerp is klaar — en dit kan écht.' : 'Uw ontwerp wordt gemaakt…'}</h2>
          <p className="text-sm text-[#202020]/70 mt-1 flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 text-[#3a190b] mt-0.5 flex-shrink-0" />Onze specialisten werken elk ontwerp <strong className="mx-1">altijd vrijblijvend</strong> verder uit op echte maten, leidingen en budget — als u dat wenst.</p>
        </div>
        <button type="button" onClick={onRequestQuote} className="inline-flex items-center justify-center gap-2 bg-[#3a190b] hover:bg-[#500000] text-white px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-colors" data-testid="request-quote-top-btn">
          Vraag offerte aan<ArrowDown className="h-4 w-4" />
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Frame src={fileUrl(session.original)} label="Uw ruimte nu" testId="original-frame" />
        {gens.slice(-1).map((g) => (
          <Frame key={g.id} src={g.status === 'done' ? fileUrl(g.path) : ''} label={g.status === 'done' ? 'Sfeerbeeld — AI-ontwerp' : 'Nieuw ontwerp'} testId="latest-frame">
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-6 text-[#3a190b]">
              {g.status === 'pending' ? (<><Loader2 className="h-8 w-8 animate-spin mb-3" /><p className="text-sm font-medium">Onze AI tekent uw gerenoveerde ruimte…</p><p className="text-xs text-[#202020]/60 mt-1">Dit duurt meestal 30 tot 90 seconden.</p></>)
                : (<><AlertCircle className="h-8 w-8 mb-3 text-red-600" /><p className="text-sm" data-testid="generation-error">{g.error}</p></>)}
            </div>
          </Frame>
        ))}
      </div>
      {gens.filter((g) => g.status === 'done').length > 0 && (
        <>
        <EmailCapture session={session} onSaved={onSessionUpdate} />
        <ShareDesign session={session} />
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[#3a190b]/70 mb-2">Uw ontwerpen — klik op uw favoriet</p>
          <div className="flex gap-3 flex-wrap" data-testid="generation-thumbs">
            {gens.filter((g) => g.status === 'done').map((g, i) => (
              <button key={g.id} type="button" onClick={() => setFavorite(g.id)} className={`relative rounded-xl overflow-hidden border-2 transition-colors ${favorite === g.id ? 'border-[#3a190b]' : 'border-transparent hover:border-[#3a190b]/40'}`} data-testid={`thumb-${i}`} aria-pressed={favorite === g.id}>
                <img src={fileUrl(g.path)} alt={`Ontwerp ${i + 1}`} className="h-24 w-32 object-cover" />
                {favorite === g.id && <Star className="absolute top-1.5 right-1.5 h-4 w-4 text-amber-400 fill-amber-400" />}
                <span className="absolute bottom-1 left-1.5 text-[10px] bg-white/90 px-1.5 rounded">Ontwerp {i + 1}</span>
              </button>
            ))}
          </div>
        </div>
        </>
      )}
      <p className="text-xs text-[#202020]/50">Sfeerbeeld: AI-impressie op basis van uw foto en keuzes. Afmetingen, leidingen en haalbaarheid controleren onze specialisten tijdens het gratis plaatsbezoek — vrijblijvend, en we sturen u uw ontwerpen ook per e-mail.</p>
    </section>
  );
};

export default ResultView;
